import React, { useState } from "react";
import { Box, Tabs, Tab } from "@mui/material";
import * as TextVariables from "../../assets/Variables/TextVariables.js";

/**
 * Generic, reusable tabs component.
 *
 * Usage:
 *   <TabsComponent
 *     idPrefix="ibs-page"
 *     tabs={[
 *       { label: "Summarized Report", content: <SummarizedReportTab /> },
 *       { label: "Full Report", content: <FullReportTab /> },
 *     ]}
 *   />
 *
 * Can be used uncontrolled (omit `value`/`onChange` and the component keeps
 * track of the active tab itself), or controlled (pass both, e.g. to sync
 * the active tab with the URL).
 *
 * `keepMounted` (default true) keeps every tab's content mounted at all
 * times (only hidden via CSS), so switching tabs never resets a tab's own
 * filters/state/scroll position - it just shows/hides it.
 */
function TabsComponent({ tabs = [], value, onChange, idPrefix = "tabs", keepMounted = true, sx }) {
  const [internalValue, setInternalValue] = useState(0);
  const activeValue = value !== undefined ? value : internalValue;

  const handleChange = (event, newValue) => {
    if (onChange) onChange(event, newValue);
    if (value === undefined) setInternalValue(newValue);
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <Tabs
          value={activeValue}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.label}
              label={tab.label}
              disabled={tab.disabled}
              id={`${idPrefix}-tab-${index}`}
              aria-controls={`${idPrefix}-tabpanel-${index}`}
              sx={{
                textTransform: "none",
                fontFamily: TextVariables.Body_3_Bold?.fontFamily,
                minHeight: 44,
              }}
            />
          ))}
        </Tabs>
      </Box>

      {tabs.map((tab, index) => {
        const isActive = activeValue === index;
        if (!keepMounted && !isActive) return null;

        return (
          <Box
            key={tab.label}
            role="tabpanel"
            hidden={!isActive}
            id={`${idPrefix}-tabpanel-${index}`}
            aria-labelledby={`${idPrefix}-tab-${index}`}
            sx={{ display: isActive ? "block" : "none", pt: 2 }}
          >
            {tab.content}
          </Box>
        );
      })}
    </Box>
  );
}

export default TabsComponent;